import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from 'components/icons';
import styles from './toast.module.scss';

export type ToastPosition = 'top' | 'left' | 'right' | 'bottom';

interface ToastProps {
  text: string;
  type?: 'success' | 'error' | 'info';
  icon?: React.ReactNode;
  position?: ToastPosition;
  duration?: number;
  id?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({
  text,
  type = 'success',
  icon,
  position = 'top',
  duration = 6000,
  id,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 50);

    // Auto close
    const closeTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for exit animation
    }, duration);

    return () => {
      clearTimeout(timer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose, id]);

  const handleManualClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  return createPortal(
    <div
      className={`${styles.toast} ${styles[position]} ${styles[type]} ${isVisible ? styles.visible : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.content}>
        {icon && <div className={styles.icon}>{icon}</div>}
        <span className={styles.text}>{text}</span>
      </div>
      <button className={styles.closeBtn} onClick={handleManualClose} aria-label="Close toast">
        <CloseIcon width="16" height="16" />
      </button>
    </div>,
    document.body,
  );
};

export default Toast;
